import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserMultiFormatReader } from '@zxing/browser';
import type { IScannerControls } from '@zxing/browser';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import { apiClient } from '@/api/axiosClient';
import type { BarcodeLookupResult } from '@/types/barcode';

interface BarcodeScanFieldProps {
  /** Scopes the lookup to what's currently on hand in this warehouse — disabled until one is picked. */
  warehouseId: string | null;
  /** Second arg is the raw scanned string — callers that must send the scan back to the server
   *  (Stock Adjustment / Stock Status Change under Barcode Required) need it, since the lookup
   *  result itself does not echo the barcode. */
  onResolved: (result: BarcodeLookupResult, barcode: string) => void;
}

/** Shared "scan a barcode to identify stock" input for Stock Adjustment/Stock Transfer — resolves
 * either a GRN-printed label (Material + Batch + current locations) or a material's own static
 * barcode (Material only) via GET /barcode/lookup. See IBarcodeLookupService server-side.
 * The scan icon opens a live camera dialog (ZXing) that auto-submits on the first successful
 * decode; typing + Enter remains available for keyboard-wedge USB scanners. */
export default function BarcodeScanField({ warehouseId, onResolved }: BarcodeScanFieldProps) {
  const { t } = useTranslation('common');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  const scan = async (barcodeOverride?: string) => {
    const barcode = (barcodeOverride ?? value).trim();
    if (!barcode || !warehouseId) return;

    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<BarcodeLookupResult>('/barcode/lookup', { params: { barcode, warehouseId } });
      if (!data.found) {
        setError(t('barcodeScan.notFound'));
        return;
      }
      onResolved(data, barcode);
      setValue('');
    } catch {
      setError(t('barcodeScan.lookupFailed'));
    } finally {
      setLoading(false);
    }
  };

  const stopCamera = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
    setCameraError(null);
    setCameraReady(false);
  };

  // Runs the live decode loop for as long as the dialog is open; a successful decode stops the
  // stream, closes the dialog and immediately submits the lookup (no extra click) per the
  // requested "scan -> auto enter apply" flow.
  useEffect(() => {
    if (!cameraOpen) return;

    let cancelled = false;
    const reader = new BrowserMultiFormatReader();

    reader
      .decodeFromVideoDevice(undefined, videoRef.current ?? undefined, (result, _err, controls) => {
        if (cancelled) return;
        controlsRef.current = controls;
        if (result) {
          controls.stop();
          controlsRef.current = null;
          setCameraOpen(false);
          setCameraReady(false);
          void scan(result.getText());
        }
        // `_err` fires on every frame with no decode found yet — not a failure, ignore it.
      })
      .then(() => {
        if (!cancelled) setCameraReady(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const name = err instanceof Error ? err.name : '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setCameraError(t('barcodeScan.cameraPermissionDenied'));
        } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
          setCameraError(t('barcodeScan.cameraNotFound'));
        } else {
          setCameraError(t('barcodeScan.cameraUnavailable'));
        }
      });

    return () => {
      cancelled = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraOpen]);

  return (
    <>
      <TextField
        label={t('barcodeScan.label')}
        placeholder={t('barcodeScan.placeholder')}
        value={value}
        size="small"
        fullWidth
        disabled={!warehouseId}
        error={Boolean(error)}
        helperText={error ?? (warehouseId ? undefined : t('barcodeScan.selectWarehouseFirst'))}
        onChange={(e) => {
          setValue(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void scan();
          }
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setError(null);
                    setCameraError(null);
                    setCameraOpen(true);
                  }}
                  disabled={loading || !warehouseId}
                >
                  {loading ? <CircularProgress size={16} /> : <QrCodeScannerOutlinedIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      <Dialog open={cameraOpen} onClose={closeCamera} maxWidth="xs" fullWidth>
        <DialogTitle>{t('barcodeScan.cameraDialogTitle')}</DialogTitle>
        <DialogContent>
          {cameraError ? (
            <Alert severity="error">{cameraError}</Alert>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <Box
                component="video"
                ref={videoRef}
                sx={{ width: '100%', borderRadius: 1, bgcolor: 'black', aspectRatio: '4 / 3', objectFit: 'cover' }}
                muted
                autoPlay
                playsInline
              />
              {!cameraReady && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">{t('barcodeScan.cameraStarting')}</Typography>
                </Box>
              )}
              {cameraReady && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  {t('barcodeScan.cameraScanningHint')}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCamera}>{t('actions.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
